import React, { useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { DataRoomShareableLink } from 'models/ShareableLink';
import ReorderResizeTable from 'components/ReorderResizeTable';
import { BODY_CONTENT_GENERATE } from './components';
import {
  CUSTOM_COL_SIZES,
  HEADING_TRANSLATIONS,
  TABLE_COLUMNS,
  SORTING_VALUES,
} from './components/data';
import { Button } from 'ncoded-component-library';
import CreateShareableLinkContext from 'router/subrouters/DataRoom/providers/CreateShareableLink/CreateShareableLink.context';
import { useTranslation } from 'react-i18next';
import SecureLinkAnalytics from '../SecureLinkAnalytics';
import { getRowBackgroundColor } from '../../utils';

import './ShareableLinksTable.styles.scss';

type ShareableLinksTableProps = {
  className?: string;
  isLoading: boolean;
  totalPages: number;
  totalItems?: number;
  shareableLinks: DataRoomShareableLink[];
  updateShareableLink: (link: DataRoomShareableLink) => void;
  onAfterToggleState?: (linkBeforeToggle: DataRoomShareableLink) => void;
};

const ShareableLinksTable: React.FC<ShareableLinksTableProps> = (props) => {
  const {
    className,
    isLoading,
    totalPages,
    totalItems,
    shareableLinks,
    updateShareableLink,
    onAfterToggleState,
  } = props;

  const { openCreateShareableLink } = useContext(CreateShareableLinkContext);

  const [expandedLinkId, setExpandedLinkId] = useState(null);

  const classes = classNames('shareable-links-table', className);

  const { t } = useTranslation();

  const renderedContent = useMemo(
    () =>
      TABLE_COLUMNS.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (link: DataRoomShareableLink) =>
            BODY_CONTENT_GENERATE[col]({
              link,
              updateShareableLink,
              onAfterToggleState,
            }),
        }),
        {},
      ),
    [updateShareableLink, onAfterToggleState],
  );

  const headerColTransformation = useMemo(
    () => ({
      linkAccess: () => (
        <Button variant="outline" onClick={() => openCreateShareableLink()}>
          {t('createSecureLink')}
        </Button>
      ),
    }),
    [openCreateShareableLink, t],
  );

  return (
    <ReorderResizeTable
      className={classes}
      columns={TABLE_COLUMNS}
      data={shareableLinks}
      isDragDisabled
      isResizeDisabled
      isDataLoading={isLoading}
      totalPages={totalPages}
      totalItems={totalItems}
      recordName="link"
      customSizes={CUSTOM_COL_SIZES}
      sortingValues={SORTING_VALUES}
      headingTranslations={HEADING_TRANSLATIONS}
      tableContentTransformations={renderedContent}
      tableHeaderContentTransformations={headerColTransformation}
      getRowBackgroundColor={getRowBackgroundColor}
      onRowClick={({ _id }) =>
        setExpandedLinkId(expandedLinkId === _id ? null : _id)
      }
      getIsRowExpanded={(row) => row.original._id === expandedLinkId}
      renderExpandedContent={(link) => <SecureLinkAnalytics link={link} />}
    />
  );
};

export default ShareableLinksTable;
